import React, { useContext, useEffect, useState } from 'react';
import { Box, Typography, Snackbar, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import StoreContext from '../../../react-core-lib/components/app/store-context';
import HandoutImageLoader from './handout-image-loader';
import UserApi from '../../../rest-api/user-api';
import DownloadUtils from '../../../utils/download-utils';
import Model3DIcon from '../../../assets/icons/3D-Preview.jpg';
import arrowRightIcon from '../../../assets/icons/Arrow-white_Right.svg';
import customiseIcon from '../../../assets/icons/CustomiseHandout.svg';
import addToBundleIcon from '../../../assets/icons/AddToBundle.svg';
import shareIcon from '../../../assets/icons/ShareEnvelop.svg';
import CTAUpgradeDialog from '../../UIControls/CTAUpgradeDialog';
import { getCTAUpgradeImageUrl } from '../../../config';
import QuickShareDialog from './quick-share-dialog';
import { getHandoutBundleUpgradeTarget } from '../../../utils/subscription-plan-utils';

const MAX_RECENT_HANDOUTS = 10;
const CARDS_PER_VIEW = 4;

const CARD_GAP = 20;

function RecentlyViewedHandouts() {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { contentStore, imageStore, authStore, userDataStore } = useContext(StoreContext);
    const [recentHandouts, setRecentHandouts] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [upgradeCTAOpen, setUpgradeCTAOpen] = useState(false);
    const [upgradeCTAClinicOpen, setUpgradeCTAClinicOpen] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarStatus, setSnackbarStatus] = useState('success');
    const [quickShareOpen, setQuickShareOpen] = useState(false);
    const [quickShareHandout, setQuickShareHandout] = useState(null);

    useEffect(() => {
        async function fetchAndStoreImages(handouts) {
            const promises = handouts.map(async (handout) => {
                const locale = 'en-UK';
                const idToken = authStore.idToken;
                const localeData = handout.contentData[locale] || [];

                const is3DModel = localeData.every((section) =>
                    section.blocks.every((block) => block.type === '3dModel' || block.type === 'documentTitle'),
                );

                if (is3DModel) {
                    const modelBlock = localeData
                        .flatMap((section) => section.blocks)
                        .find((block) => block.type === '3dModel');

                    if (modelBlock && modelBlock.imageSrc) {
                        const imageSrc = modelBlock.imageSrc;
                        const imageObject = await imageStore.getImageFromIndexedDB(imageSrc);

                        if (!imageObject) {
                            try {
                                const payload = { files: [{ method: 'get', key: imageSrc }], contentCategory: 'image' };
                                const presignedUrl = await UserApi.generatePresignedUrls(idToken, payload);
                                const downloadResult = await DownloadUtils.downloadImageFromPresignedUrl(
                                    presignedUrl.getUrls[0]['presignedUrl'],
                                );
                                if (downloadResult && downloadResult.base64data) {
                                    await imageStore.saveImageToIndexedDB(
                                        imageSrc,
                                        downloadResult.base64data,
                                        downloadResult.etag,
                                    );
                                    handout.previewImage = downloadResult.base64data;
                                } else {
                                    handout.previewImage = Model3DIcon;
                                }
                            } catch (error) {
                                console.error(`Error fetching image: ${error}`);
                                handout.previewImage = Model3DIcon;
                            }
                        } else {
                            handout.previewImage = imageObject.data;
                        }
                    } else {
                        handout.previewImage = Model3DIcon;
                    }
                } else {
                    const firstIllustration = localeData
                        .flatMap((section) => section.blocks)
                        .find((block) => block.type === 'illustration');

                    if (firstIllustration) {
                        const imageSrc = firstIllustration.src;
                        const imageObject = await imageStore.getImageFromIndexedDB(imageSrc);
                        if (!imageObject) {
                            try {
                                const payload = { files: [{ method: 'get', key: imageSrc }], contentCategory: 'image' };
                                const presignedUrl = await UserApi.generatePresignedUrls(idToken, payload);
                                const downloadResult = await DownloadUtils.downloadImageFromPresignedUrl(
                                    presignedUrl.getUrls[0]['presignedUrl'],
                                );
                                if (downloadResult) {
                                    await imageStore.saveImageToIndexedDB(
                                        imageSrc,
                                        downloadResult.base64data,
                                        downloadResult.etag,
                                    );
                                }
                            } catch (error) {
                                console.error(`Error fetching image: ${error}`);
                            }
                        }
                    }
                }
            });

            await Promise.all(promises);
            setRecentHandouts(handouts);
        }

        const recentHandoutIds = JSON.parse(localStorage.getItem('recentHandouts')) || [];
        const fetchedHandouts = contentStore.getContentFromIds(recentHandoutIds).slice(0, MAX_RECENT_HANDOUTS);
        fetchAndStoreImages(fetchedHandouts);
    }, [contentStore, imageStore, authStore]);

    const cardsPerView = isMobile ? 1 : CARDS_PER_VIEW;
    const canScrollLeft = currentIndex > 0;
    const canScrollRight = currentIndex + cardsPerView < recentHandouts.length;
    const hasMoreThanCards = recentHandouts.length > cardsPerView;

    const handlePrev = () => {
        if (canScrollLeft) setCurrentIndex((i) => i - 1);
    };

    const handleNext = () => {
        if (canScrollRight) setCurrentIndex((i) => i + 1);
    };

    const handleCardClick = (handout) => {
        navigate(`/handout/${handout.contentId}`);
    };

    const handleAddToBundle = async (e, handout) => {
        e.stopPropagation();
        const isMultiUser = userDataStore?.subscriptionData?.accountType === 'Clinic-multiuser';

        const upgradeTarget = getHandoutBundleUpgradeTarget(userDataStore?.subscriptionData?.accountType);
        if (upgradeTarget === 'professional') {
            setUpgradeCTAOpen(true);
            return;
        }
        if (upgradeTarget === 'clinic') {
            setUpgradeCTAClinicOpen(true);
            return;
        }

        const userHandoutsJson = userDataStore.userData.handouts || '[]';
        let userHandouts = Array.isArray(userDataStore.userData.handouts)
            ? userDataStore.userData.handouts
            : JSON.parse(userHandoutsJson);

        const existingContentIds = userHandouts.map((h) => h.contentId);
        if (existingContentIds.includes(handout.contentId)) {
            setSnackbarMessage('Handout already exists in the bundle');
            setSnackbarStatus('error');
            setSnackbarOpen(true);
            return;
        }
        if (userHandouts.length >= 5) {
            setSnackbarMessage('Cannot add more than 5 handouts to the bundle');
            setSnackbarStatus('error');
            setSnackbarOpen(true);
            return;
        }

        userHandouts.push({ contentId: handout.contentId, contentName: handout.contentName, categories: '' });
        const payload = { handouts: userHandouts };

        try {
            if (isMultiUser) {
                payload.action = 'updateMultiuser';
                payload.emailAddress = userDataStore.userData.emailAddress;
                payload.inviteId = userDataStore.userData.inviteId;
                await UserApi.updateClinicianDataMultiuser(authStore.idToken, payload);
            } else {
                await UserApi.updateClinicianData(authStore.idToken, payload);
            }
            userDataStore.updateUserHandouts(userHandouts);
            setSnackbarMessage('Handout added to bundle');
            setSnackbarStatus('success');
            setSnackbarOpen(true);
        } catch (error) {
            console.error('Error adding handout to bundle:', error);
            setSnackbarMessage('Failed to add handout to bundle');
            setSnackbarStatus('error');
            setSnackbarOpen(true);
        }
    };

    const handleQuickShare = (e, handout) => {
        e.stopPropagation();
        if (
            userDataStore?.subscriptionData?.accountType === 'Free' ||
            userDataStore?.subscriptionData?.accountType === 'Student'
        ) {
            setUpgradeCTAOpen(true);
            return;
        }
        setQuickShareHandout(handout);
        setQuickShareOpen(true);
    };

    // Navigate to handout and activate fresh Customise mode (CETRA-738)
    const handleCustomise = (e, handout) => {
        e.stopPropagation();
        localStorage.removeItem('loadCustomisedHandout');
        localStorage.setItem('openInCustomiseMode', 'true');
        navigate(`/handout/${handout.contentId}`);
    };

    if (recentHandouts.length === 0) {
        return null;
    }

    const visibleHandouts = recentHandouts.slice(currentIndex, currentIndex + cardsPerView);

    return (
        <>
            <Box
                sx={{
                    maxWidth: '1440px',
                    margin: '0 auto',
                    width: '100%',
                    px: { xs: '22px', md: '40px' },
                    pb: { xs: '16px', md: '21px' },
                    boxSizing: 'border-box',
                }}
            >
                {/* Section container */}
                <Box
                    sx={{
                        background: '#F0F0FC',
                        borderRadius: { xs: '24px', md: '30px' },
                        padding: { xs: '20px', md: '30px' },
                        position: 'relative',
                        overflow: { xs: 'visible', md: 'hidden' },
                    }}
                >
                    <Typography
                        sx={{
                            fontFamily: 'Inter, sans-serif',
                            fontWeight: 600,
                            fontSize: { xs: '24px', md: '40px' },
                            lineHeight: { xs: '28px', md: '40px' },
                            letterSpacing: { xs: '-0.6px', md: '-1.6px' },
                            color: '#000453',
                            mb: { xs: '16px', md: '20px' },
                        }}
                    >
                        Recently viewed
                    </Typography>

                    {/* Cards row + arrows */}
                    <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                        {/* Left arrow */}
                        <Box
                            sx={{
                                position: 'absolute',
                                left: '-20px',
                                zIndex: 10,
                                width: { xs: 44, md: 50 },
                                height: { xs: 44, md: 50 },
                                borderRadius: '50%',
                                background: hasMoreThanCards && canScrollLeft ? '#FF669B' : '#FFB3CE',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: hasMoreThanCards && canScrollLeft ? 'pointer' : 'default',
                                flexShrink: 0,
                                ...(hasMoreThanCards && canScrollLeft && { '&:hover': { background: '#FF85B0' } }),
                            }}
                            onClick={handlePrev}
                        >
                            <img
                                src={arrowRightIcon}
                                alt="Previous"
                                width={18}
                                style={{ transform: 'scaleX(-1)', display: 'block' }}
                            />
                        </Box>

                        {/* Cards — 1fr so all 4 fill the available width evenly */}
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: {
                                    xs: 'minmax(0, 1fr)',
                                    md: `repeat(${cardsPerView}, minmax(0, 1fr))`,
                                },
                                gap: { xs: '16px', md: `${CARD_GAP}px` },
                                px: { xs: 0, md: 0 },
                                maxWidth: { xs: '314px', md: 'none' },
                                margin: { xs: '0 auto', md: 0 },
                                flex: 1,
                                minWidth: 0,
                            }}
                        >
                            {visibleHandouts.map((handout) => (
                                <HandoutCard
                                    key={handout.contentId}
                                    handout={handout}
                                    imageStore={imageStore}
                                    authStore={authStore}
                                    onClick={() => handleCardClick(handout)}
                                    onAddToBundle={(e) => handleAddToBundle(e, handout)}
                                    onQuickShare={(e) => handleQuickShare(e, handout)}
                                    onCustomise={(e) => handleCustomise(e, handout)}
                                />
                            ))}
                        </Box>

                        {/* Right arrow */}
                        <Box
                            sx={{
                                position: 'absolute',
                                right: '-20px',
                                zIndex: 10,
                                width: { xs: 44, md: 50 },
                                height: { xs: 44, md: 50 },
                                borderRadius: '50%',
                                background: hasMoreThanCards && canScrollRight ? '#FF669B' : '#FFB3CE',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: hasMoreThanCards && canScrollRight ? 'pointer' : 'default',
                                flexShrink: 0,
                                ...(hasMoreThanCards && canScrollRight && { '&:hover': { background: '#FF85B0' } }),
                            }}
                            onClick={handleNext}
                        >
                            <img src={arrowRightIcon} alt="Next" width={18} style={{ display: 'block' }} />
                        </Box>
                    </Box>
                </Box>
            </Box>

            <CTAUpgradeDialog
                open={upgradeCTAClinicOpen}
                handleClose={() => setUpgradeCTAClinicOpen(false)}
                onConfirm={() => navigate('/settings/manage-subscription')}
                title="Upgrade now for clinic-wide access and advanced features!"
                message="You've reached a feature that's beyond your current plan. Upgrade today to add more users and enjoy full access to our features!"
                buttonText="Upgrade plan"
                benefits={[
                    'Unlimited digital sharing of all handouts & anatomy diagrams',
                    'Bundling of handouts and diagrams for seamless sharing',
                    'Personalise with your clinic logo',
                ]}
                imageUrl={getCTAUpgradeImageUrl('clinic')}
            />

            <CTAUpgradeDialog
                open={upgradeCTAOpen}
                handleClose={() => setUpgradeCTAOpen(false)}
                onConfirm={() => navigate('/settings/manage-subscription')}
                title="Unlock unlimited sharing and more with just one click!"
                message="You've reached a feature that's beyond your current plan. Upgrade today to enjoy full access!"
                buttonText="Upgrade plan"
                benefits={[
                    'Unlimited digital sharing of all handouts',
                    'Unlimited digital sharing of all anatomy diagrams',
                    'Personalise with your clinic details',
                ]}
                imageUrl={getCTAUpgradeImageUrl('professional')}
            />

            {quickShareHandout && (
                <QuickShareDialog
                    open={quickShareOpen}
                    handout={quickShareHandout}
                    onClose={() => {
                        setQuickShareOpen(false);
                        setQuickShareHandout(null);
                    }}
                />
            )}

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={() => setSnackbarOpen(false)}
                message={
                    <Box display="flex" alignItems="center">
                        {snackbarMessage}
                        {snackbarStatus === 'success' ? (
                            <CheckIcon sx={{ ml: 1, color: 'white' }} />
                        ) : (
                            <CloseIcon sx={{ ml: 1, color: 'red' }} />
                        )}
                    </Box>
                }
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                sx={{
                    '& .MuiSnackbarContent-root': {
                        backgroundImage: 'linear-gradient(132deg, #FA96C3 -2.07%, #FF669B 15.82%, #BD8DE8 116.64%)',
                        display: 'flex',
                        justifyContent: 'center',
                        borderRadius: 2,
                    },
                }}
            />
        </>
    );
}

function HandoutCard({ handout, imageStore, authStore, onClick, onAddToBundle, onQuickShare, onCustomise }) {
    return (
        <Box
            sx={{
                height: { xs: '414px', md: '414px' },
                width: { xs: '100%', md: 'auto' },
                maxWidth: { xs: '314px', md: 'none' },
                background: '#FFFFFF',
                borderRadius: '20px',
                display: 'flex',
                flexDirection: 'column',
                cursor: 'pointer',
                minWidth: 0,
                overflow: 'hidden',
            }}
            onClick={onClick}
        >
            {/* Image area — 14px padding, image contained within */}
            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    padding: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <HandoutImageLoader
                    src={(() => {
                        const locale = 'en-UK';
                        const localeData = handout.contentData[locale] || [];
                        const is3DModel = localeData.every((s) =>
                            s.blocks.every((b) => b.type === '3dModel' || b.type === 'documentTitle'),
                        );
                        if (is3DModel) {
                            const modelBlock = localeData.flatMap((s) => s.blocks).find((b) => b.type === '3dModel');
                            return modelBlock?.imageSrc || null;
                        }
                        const illustration = localeData.flatMap((s) => s.blocks).find((b) => b.type === 'illustration');
                        return illustration?.src || null;
                    })()}
                    alt={handout.contentName}
                    imageStore={imageStore}
                    handoutData={handout}
                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block' }}
                />
            </Box>

            {/* Title */}
            <Box sx={{ px: '14px', pt: '12px', pb: '36px', flexShrink: 0 }}>
                <Typography
                    sx={{
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 600,
                        fontSize: '22px',
                        lineHeight: '26px',
                        letterSpacing: '-0.44px',
                        color: '#000453',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        minHeight: '52px',
                    }}
                >
                    {handout.contentName}
                </Typography>
            </Box>

            {/* Separator */}
            <Box
                sx={{
                    mx: '14px',
                    height: '2px',
                    background: 'rgba(0, 4, 83, 0.10)',
                    borderRadius: '1px',
                    flexShrink: 0,
                }}
            />

            {/* Action buttons: customise left, plus+share grouped right */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: '14px',
                    py: '14px',
                    flexShrink: 0,
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Left: Customise */}
                <Tooltip title="Open in customise mode" arrow>
                    <Box
                        sx={{
                            width: 50,
                            height: 50,
                            borderRadius: '50%',
                            background: '#FF669B',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            '&:hover': { background: '#FF85B0' },
                        }}
                        onClick={onCustomise}
                    >
                        <img src={customiseIcon} alt="Customise" width={23} height={23} />
                    </Box>
                </Tooltip>

                {/* Right: Plus + Share grouped with 10px gap */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <Tooltip title="Add to bundle" arrow>
                        <Box
                            sx={{
                                width: 50,
                                height: 50,
                                borderRadius: '50%',
                                background: '#FF669B',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                '&:hover': { background: '#FF85B0' },
                            }}
                            onClick={onAddToBundle}
                        >
                            <img src={addToBundleIcon} alt="Add to bundle" width={22} height={22} />
                        </Box>
                    </Tooltip>
                    <Tooltip title="Quick share" arrow>
                        <Box
                            sx={{
                                width: 50,
                                height: 50,
                                borderRadius: '50%',
                                background: '#FF669B',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                '&:hover': { background: '#FF85B0' },
                            }}
                            onClick={onQuickShare}
                        >
                            <img src={shareIcon} alt="Share" width={24} height={22} />
                        </Box>
                    </Tooltip>
                </Box>
            </Box>
        </Box>
    );
}

export default observer(RecentlyViewedHandouts);
